import type { InfiniteData, Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { matchQuery } from '@tanstack/react-query';

import type { ArchivedSessionsPage } from '../queries';
import type { CodeSessionRow, CodeSessionSummary } from '../types';
import type { SavedSessionRow, SavedSessionTitle } from './types';

import { codeSessionKeys, mergeSessionSummaries, toSessionSummary } from '../queries';
import { isLocalSessionId } from './session-id';

import { fixSessionKeys } from '@/data/fix-sessions/queries';

export type SessionLists = {
  add: (client: QueryClient, session: CodeSessionRow) => Promise<void>;
  update: (client: QueryClient, row: SavedSessionRow) => Promise<void>;
  archived: (client: QueryClient, row: SavedSessionRow) => Promise<void>;
  unarchived: (client: QueryClient, row: SavedSessionRow) => Promise<void>;
  settle: (client: QueryClient) => Promise<void>;
  /** Reads the lists again where live events changed the session's status or a question on it. */
  invalidate: (
    client: QueryClient,
    row: Pick<CodeSessionRow, 'installationId'>,
    change: { status: boolean; question: boolean },
  ) => void;
  retitle: (client: QueryClient, sessionId: string, saved: SavedSessionTitle) => Promise<void>;
  /** The session's cached row, for a session whose view is not cached. */
  find: (client: QueryClient, sessionId: string) => CodeSessionSummary | undefined;
};

function prependNewSession(client: QueryClient, queryKey: QueryKey, session: CodeSessionRow) {
  client.setQueryData<CodeSessionSummary[]>(queryKey, (rows) => [
    // A new session's branch has no pull request yet.
    toSessionSummary(session, null),
    ...(rows ?? []).filter((row) => row.id !== session.id),
  ]);
  void client.invalidateQueries({ queryKey });
}

/** Cancels the reads under way first: they began before the save and could bring the old row back. */
async function patchListedRow(
  client: QueryClient,
  prefix: QueryKey,
  sessionId: string,
  fields: Partial<Pick<CodeSessionSummary, 'title' | 'status' | 'awaitingInputAt'>>,
  updatedAt: string,
) {
  const keys = client
    .getQueriesData<CodeSessionSummary[]>({ queryKey: prefix })
    .filter(([, rows]) => rows?.some((row) => row.id === sessionId))
    .map(([queryKey]) => queryKey);
  await Promise.all(keys.map((queryKey) => client.cancelQueries({ queryKey, exact: true })));
  for (const queryKey of keys) {
    client.setQueryData<CodeSessionSummary[]>(queryKey, (rows) =>
      rows?.map((row) =>
        row.id === sessionId && row.updatedAt <= updatedAt ? { ...row, ...fields, updatedAt } : row,
      ),
    );
  }
}

/** Every archived session cached for the archive and its searches. */
function cachedArchivedSessions(client: QueryClient): CodeSessionSummary[] {
  return client
    .getQueriesData<InfiniteData<ArchivedSessionsPage>>({ queryKey: codeSessionKeys.archivedLists })
    .flatMap(([, data]) => data?.pages.flatMap((page) => page.sessions) ?? []);
}

/** Drop a restored session from the archive's pages before their refetch lands. */
function removeArchivedSession(client: QueryClient, sessionId: string) {
  client.setQueriesData<InfiniteData<ArchivedSessionsPage>>(
    { queryKey: codeSessionKeys.archivedLists },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          sessions: page.sessions.filter((row) => row.id !== sessionId),
        })),
      },
  );
}

const cloudLists: SessionLists = {
  add: async (client, session) => {
    await client.cancelQueries({
      queryKey: codeSessionKeys.list(session.installationId),
      exact: true,
    });
    prependNewSession(client, codeSessionKeys.list(session.installationId), session);
  },
  /**
   * Writes a session's row into its list, adding the session when the list does not hold it, as one
   * opened from a link, older than the list's last row, or just unarchived. The row brings its
   * creator's ID, so an added row says at once who started it; their profile waits for the next
   * list read.
   */
  update: async (client, summary) => {
    const key = codeSessionKeys.list(summary.installationId);
    const cached = client.getQueryData<CodeSessionSummary[]>(key);
    // Session lists leave side agents out; they open from their main session.
    if (summary.sideAgentParentId || cached === undefined) {
      return;
    }
    // A read under way can land without a row the list lacks, and the merge keeps the incoming
    // membership (`mergeSessionSummaries`): cancel it before writing, since cancellation can
    // restore its pre-fetch snapshot. A listed row needs no cancel, since the merge keeps the
    // newer row by `updatedAt`, so the sidebar's poll lands; a cancelled poll is not rerun.
    if (!cached.some((row) => row.id === summary.id)) {
      await client.cancelQueries({ queryKey: key, exact: true });
    }
    client.setQueryData<CodeSessionSummary[]>(key, (rows) => {
      if (rows === undefined) {
        return undefined;
      }
      const listed = rows.find((row) => row.id === summary.id);
      // A session row carries no pull request; the list's read of the mirror stands.
      const pullRequest = listed?.pullRequest ?? null;
      // Nor the agents at work on tasks it sent, which the list read too; it does carry its check-in.
      const agentsAtWork = listed?.agentsAtWork;
      return mergeSessionSummaries(rows, [
        // Nor whose profile started it, which the list read too.
        toSessionSummary({ ...summary, agentsAtWork }, pullRequest, listed?.createdBy),
        ...rows.filter((row) => row.id !== summary.id),
      ]);
    });
  },
  archived: (client, row) => cloudLists.update(client, row),
  unarchived: async (client, row) => {
    removeArchivedSession(client, row.id);
    await cloudLists.update(client, row);
  },
  settle: async (client) => {
    void client.invalidateQueries({ queryKey: codeSessionKeys.lists });
    void client.invalidateQueries({ queryKey: codeSessionKeys.archivedLists });
    // Automations lists the sessions cubic started for authors. Awaited, so its overlay hides
    // the moving row until the reread lands.
    await client.invalidateQueries({ queryKey: fixSessionKeys.list });
  },
  invalidate: (client, row, change) => {
    if (change.status || change.question) {
      void client.invalidateQueries({
        queryKey: codeSessionKeys.list(row.installationId),
        exact: true,
      });
    }
  },
  retitle: async (client, sessionId, { title, updatedAt }) => {
    await patchListedRow(client, codeSessionKeys.lists, sessionId, { title }, updatedAt);
    // The archive's searches match on titles, so it reads its pages again rather than keep a
    // renamed row under a search it no longer matches.
    void client.invalidateQueries({ queryKey: codeSessionKeys.archivedLists });
  },
  find: (client, sessionId) =>
    [
      ...client
        .getQueriesData<CodeSessionSummary[]>({ queryKey: codeSessionKeys.lists })
        .flatMap(([, rows]) => rows ?? []),
      ...cachedArchivedSessions(client),
    ].find((row) => row.id === sessionId),
};

/** This computer's one list holds its archived sessions too, which their status files. */
function fileByStatus(client: QueryClient, row: SavedSessionRow) {
  return patchListedRow(
    client,
    codeSessionKeys.local,
    row.id,
    { status: row.status, awaitingInputAt: row.awaitingInputAt },
    row.updatedAt,
  );
}

const localLists: SessionLists = {
  // The sidebar lists sessions on this computer from their own query, never from the cloud lists
  // it saves in the browser, and sends cubic's server nothing of them.
  add: async (client, session) => prependNewSession(client, codeSessionKeys.local, session),
  // A read of one session brings its row as OpenCode holds it now, ahead of the list's next read:
  // the listed row follows it at once, when the row is newer, and a list read under way goes on,
  // since the list keeps the newer of two rows (`localCodeSessionsOptions`).
  // A session the list lacks waits for that read. A question that arrives leaves the version as it
  // is, and the stream reads the list again for it (`connection.ts`).
  update: async (client, row) => {
    client.setQueryData<CodeSessionSummary[]>(codeSessionKeys.local, (rows) =>
      rows?.map((listed) =>
        listed.id === row.id && listed.updatedAt < row.updatedAt
          ? {
              ...listed,
              title: row.title,
              status: row.status,
              awaitingInputAt: row.awaitingInputAt,
              lastActivityAt: row.lastActivityAt,
              rewindTurnId: row.rewindTurnId,
              updatedAt: row.updatedAt,
            }
          : listed,
      ),
    );
  },
  archived: (client, row) => fileByStatus(client, row),
  unarchived: (client, row) => fileByStatus(client, row),
  // Not awaited: the archive or unarchive placed the row already, so no overlay waits on this read.
  settle: async (client) => {
    void client.invalidateQueries({ queryKey: codeSessionKeys.local, exact: true });
  },
  // Asks reach this list through the page's event stream, which reads it again itself
  // (`connection.ts`).
  invalidate: (client, _row, change) => {
    if (change.status) {
      void client.invalidateQueries({ queryKey: codeSessionKeys.local, exact: true });
    }
  },
  retitle: (client, sessionId, { title, updatedAt }) =>
    patchListedRow(client, codeSessionKeys.local, sessionId, { title }, updatedAt),
  find: (client, sessionId) =>
    client
      .getQueryData<CodeSessionSummary[]>(codeSessionKeys.local)
      ?.find((row) => row.id === sessionId),
};

/**
 * Not on `SessionRuntime`: `queries.ts` imports the runtime modules, so cache code there would make
 * an import cycle.
 */
export function listsFor(sessionId: string): SessionLists {
  return isLocalSessionId(sessionId) ? localLists : cloudLists;
}

/** Whether `query` holds rows that a runtime's `find` reads. */
export function isSessionListQuery(query: Query): boolean {
  return (
    matchQuery({ queryKey: codeSessionKeys.lists }, query) ||
    matchQuery({ queryKey: codeSessionKeys.archivedLists }, query) ||
    matchQuery({ queryKey: codeSessionKeys.local, exact: true }, query)
  );
}
